import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { 
  Lock, Mail, ArrowRight, AlertCircle, 
  Loader2, CheckCircle2, Terminal 
} from "lucide-react";
import "./AdminLogin.css";

import { API_BASE } from "../../../config/api";

export default function AdminLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });

  // ✅ Get message from registration redirect
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      if (location.state?.email) {
        setFormData(prev => ({ ...prev, email: location.state.email }));
      }
      // Clear the state so message doesn't persist on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (!formData.email || !formData.password) {
      setError("Email and password are required.");
      setLoading(false);
      return;
    }

    try {
      console.log("🔑 Admin login attempt:", formData.email);
      
      const response = await axios.post(`${API_BASE}/api/admin/auth/login`, {
        email: formData.email.toLowerCase().trim(),
        password: formData.password
      });

      console.log("✅ Login successful:", response.data);

      // ✅ Store admin data in localStorage
      const { admin, token } = response.data;
      localStorage.setItem("adminToken", token);
      localStorage.setItem("adminData", JSON.stringify(admin));
      localStorage.setItem("isAdmin", "true");

      setSuccessMessage("✅ Login successful! Redirecting to dashboard...");

      // ✅ Redirect to admin dashboard after 1.5 seconds
      setTimeout(() => {
        navigate("/admin/dashboard", { replace: true });
      }, 1500);

    } catch (err) {
      console.error("❌ Login failed:", err);
      
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.response?.status === 401) {
        setError("Invalid credentials. Please check your email and password.");
      } else if (err.response?.status === 404) {
        setError("Admin account not found. Please register first.");
      } else {
        setError("Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login-page">
      <div className="admin-glow node-amber"></div>
      <div className="admin-glow node-slate"></div>

      <div className="admin-login-card">
        <div className="admin-accent-strip"></div>

        <header className="admin-card-header">
          <div className="admin-badge">
            <Terminal size={12} />
            <span>Root Authority Terminal</span>
          </div>
          <h2>Administrative Login</h2>
          <p>Authenticate with system credentials to access the control plane.</p>
        </header>

        {error && (
          <div className="admin-error-banner" role="alert">
            <AlertCircle size={16} />
            <span>{error}</span>
            <button className="error-close" onClick={() => setError("")}>×</button>
          </div>
        )}

        {successMessage && (
          <div className="admin-success-banner" role="status">
            <CheckCircle2 size={16} />
            <span>{successMessage}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate className="admin-form">
          <div className="admin-input-group">
            <label htmlFor="email">Administrative Email</label>
            <div className="input-field-wrapper">
              <Mail size={16} className="field-icon" />
              <input
                id="email"
                name="email"
                type="email"
                placeholder="operator@erax-invest.com"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={loading}
                autoComplete="email"
              />
            </div>
          </div>

          <div className="admin-input-group">
            <label htmlFor="password">Password</label>
            <div className="input-field-wrapper">
              <Lock size={16} className="field-icon" />
              <input
                id="password"
                name="password"
                type="password"
                placeholder="••••••••••••"
                value={formData.password}
                onChange={handleChange}
                required
                disabled={loading}
                autoComplete="current-password"
              />
            </div>
          </div>

          <button 
            type="submit" 
            className="admin-submit-btn interactive-shimmer"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 size={18} className="spin" />
                <span>Authenticating...</span>
              </>
            ) : (
              <>
                <span>Establish Connection</span>
                <ArrowRight size={18} className="arrow-icon" />
              </>
            )}
          </button>
        </form>

        <footer className="admin-card-footer">
          <p>
            New operator?{" "}
            <Link to="/adminRegistration">Register Admin Profile</Link>
          </p>
        </footer>
      </div>
    </div>
  );
}